/**
 * Load baked lightmap build-data from a zip bundle beside the scene file.
 */
import { unzipSync } from 'fflate';

import { projectInfo } from '../storage/projectContext.js';
import { AssetPath } from '../storage/storageProvider.js';
import { parseJson } from '../utils/Utils.js';

import { buildDataZipPathForScene } from './paths.js';

import type { LightmapManifest } from './lightmapApply.js';

export { BUILD_DATA_ZIP_EXT } from '../const.js';
export { buildDataZipPathForScene } from './paths.js';

const MANIFEST_PATH = 'manifest.json';

function normalizeZipPath(path: string): string {
  return path.replace(/\\/g, '/');
}

export type LightmapTextureFormat = 'exr' | 'hdr' | 'jpeg' | 'jpg' | 'png';

/** Infer baked lightmap format from the manifest-relative zip path. */
export function lightmapFormatFromPath(relativePath: string): LightmapTextureFormat | null {
  const lower = normalizeZipPath(relativePath).toLowerCase();
  if (lower.endsWith('.exr')) {
    return 'exr';
  }
  if (lower.endsWith('.hdr')) {
    return 'hdr';
  }
  if (lower.endsWith('.jpeg')) {
    return 'jpeg';
  }
  if (lower.endsWith('.jpg')) {
    return 'jpg';
  }
  if (lower.endsWith('.png')) {
    return 'png';
  }
  return null;
}

function lightmapBlobMimeType(format: LightmapTextureFormat | null): string {
  switch (format) {
    case 'png':
      return 'image/png';
    case 'jpeg':
    case 'jpg':
      return 'image/jpeg';
    case 'hdr':
      return 'image/vnd.radiance';
    case 'exr':
    default:
      return 'application/octet-stream';
  }
}

export function buildDataZipAssetPathForScene(sceneAssetPath: string): string {
  return buildDataZipPathForScene(sceneAssetPath);
}

export class LightmapBuildData {
  private readonly blobUrls = new Map<string, string>();

  constructor(
    readonly manifest: LightmapManifest,
    private readonly files: Map<string, Uint8Array>,
  ) {}

  getUv2Json<T>(relativePath: string): T {
    const bytes = this.getFile(relativePath);
    const text = new TextDecoder().decode(bytes);
    return parseJson<T>(text);
  }

  getLightmapBlobUrl(relativePath: string): string {
    const key = normalizeZipPath(relativePath);
    const existing = this.blobUrls.get(key);
    if (existing) {
      return existing;
    }
    const bytes = this.getFile(relativePath);
    const mimeType = lightmapBlobMimeType(lightmapFormatFromPath(relativePath));
    const blob = new Blob([Uint8Array.from(bytes)], { type: mimeType });
    const url = URL.createObjectURL(blob);
    this.blobUrls.set(key, url);
    return url;
  }

  dispose(): void {
    for (const url of this.blobUrls.values()) {
      URL.revokeObjectURL(url);
    }
    this.blobUrls.clear();
  }

  private getFile(relativePath: string): Uint8Array {
    const key = normalizeZipPath(relativePath);
    const bytes = this.files.get(key);
    if (!bytes) {
      throw new Error(`Missing file in build-data zip: ${key}`);
    }
    return bytes;
  }
}

export async function loadLightmapBuildData(zipAssetPath: string): Promise<LightmapBuildData> {
  const provider = projectInfo.storageProvider;
  if (!provider) {
    throw new Error('No storage provider available to load lightmap build-data');
  }

  const assetPath = AssetPath.fromString(zipAssetPath);
  const buffer = await provider.downloadFileAsBuffer(assetPath);
  const zipBytes = new Uint8Array(buffer);
  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(zipBytes);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to unzip ${zipAssetPath}: ${message}`);
  }

  const files = new Map<string, Uint8Array>();
  for (const [path, bytes] of Object.entries(entries)) {
    files.set(normalizeZipPath(path), bytes);
  }

  const manifestBytes = files.get(MANIFEST_PATH);
  if (!manifestBytes) {
    throw new Error(`Build-data zip missing ${MANIFEST_PATH}`);
  }
  const manifestText = new TextDecoder().decode(manifestBytes);
  const manifest = parseJson<LightmapManifest>(manifestText);

  return new LightmapBuildData(manifest, files);
}

function parentDirAndFileName(assetPath: string): { parentDir: string; fileName: string } | null {
  const normalized = assetPath.replace(/\\/g, '/');
  const slash = normalized.lastIndexOf('/');
  if (slash < 0) {
    return null;
  }
  return { parentDir: normalized.slice(0, slash), fileName: normalized.slice(slash + 1) };
}

export async function lightmapBuildDataExists(zipAssetPath: string): Promise<boolean> {
  const provider = projectInfo.storageProvider;
  if (!provider) {
    return false;
  }

  const parts = parentDirAndFileName(zipAssetPath);
  if (!parts) {
    return false;
  }

  try {
    const listing = await provider.listFiles(AssetPath.fromString(parts.parentDir), false, false);
    return listing.files.some((file) => file.name === parts.fileName);
  } catch {
    // DefaultStorageProvider and other minimal providers do not support listFiles.
    try {
      return await provider.exists(AssetPath.fromString(zipAssetPath));
    } catch {
      return false;
    }
  }
}
